import {
  REGISTER_ACCOUNTS_USER,
  REGISTER_ACCOUNTS_USER_SUCCESS,
  REGISTER_ACCOUNTS_USER_FAILURE,
  ONBOARDING_HYDRATE_REQUEST,
  ONBOARDING_HYDRATE_SUCCESS,
  ONBOARDING_HYDRATE_FAILURE,
  ONBOARDING_SAVE_BUSINESS_REQUEST,
  ONBOARDING_SAVE_BUSINESS_SUCCESS,
  ONBOARDING_SAVE_BUSINESS_FAILURE,
  ONBOARDING_SAVE_PRODUCTION_REQUEST,
  ONBOARDING_SAVE_PRODUCTION_SUCCESS,
  ONBOARDING_SAVE_PRODUCTION_FAILURE,
  ONBOARDING_SAVE_SALES_REQUEST,
  ONBOARDING_SAVE_SALES_SUCCESS,
  ONBOARDING_SAVE_SALES_FAILURE,
  ONBOARDING_FINALIZE_REQUEST,
  ONBOARDING_FINALIZE_SUCCESS,
  ONBOARDING_FINALIZE_FAILURE,
  ONBOARDING_SAVE_BRANDING_REQUEST,
  ONBOARDING_SAVE_BRANDING_SUCCESS,
  ONBOARDING_SAVE_BRANDING_FAILURE,
  ONBOARDING_SAVE_PRICING_REQUEST,
  ONBOARDING_SAVE_PRICING_SUCCESS,
  ONBOARDING_SAVE_PRICING_FAILURE,
  ONBOARDING_SAVE_PRODUCTS_REQUEST,
  ONBOARDING_SAVE_PRODUCTS_SUCCESS,
  ONBOARDING_SAVE_PRODUCTS_FAILURE,
  ONBOARDING_SAVE_PRINT_OPTIONS_REQUEST,
  ONBOARDING_SAVE_PRINT_OPTIONS_SUCCESS,
  ONBOARDING_SAVE_PRINT_OPTIONS_FAILURE,
  ONBOARDING_SAVE_STORE_REQUEST,
  ONBOARDING_SAVE_STORE_SUCCESS,
  ONBOARDING_SAVE_STORE_FAILURE,
  ONBOARDING_ENRICH_WEBSITE_REQUEST,
  ONBOARDING_ENRICH_WEBSITE_SUCCESS,
  ONBOARDING_ENRICH_WEBSITE_FAILURE,
} from "../../constants/actionTypes";

/**
 * Onboarding-wizard state slice (steps 2-5) + accounts-register
 * sub-state (step 1). Kept in a single reducer because the lifecycle
 * is end-to-end-coupled: a successful register dispatch immediately
 * leads to a hydrate, and a successful finalize closes the whole flow.
 *
 * Per-step loading flags so a slow save doesn't make the entire shell
 * look stuck. Errors are stored as strings ready to display.
 *
 * `currentStep` is the server-controlled progression key:
 *   'business'   — Step 2 form open
 *   'production' — Step 3 form open
 *   'sales'      — Step 4 form open
 *   'branding'   — Step 5 form open
 *   'complete'   — wizard closed, dashboard available
 */
const initialState = {
  // Step 1
  registerLoading: false,
  registerError: null,

  // Steps 2-5 wizard state
  hydrated: false,
  hydrating: false,
  hydrateError: null,
  currentStep: null,

  business: null,
  businessSaving: false,
  businessError: null,

  production: null,
  productionSaving: false,
  productionError: null,

  sales: null,
  salesSaving: false,
  salesError: null,

  branding: null,
  brandingSaving: false,
  brandingError: null,
  enterpriseId: null,

  pricingTableIds: [],
  pricingSaving: false,
  pricingError: null,

  blankProducts: [],
  productsSaving: false,
  productsError: null,

  categoryConfig: {},
  printOptionsSaving: false,
  printOptionsError: null,

  createStore: false,
  storeSaving: false,
  storeError: null,

  finalizing: false,
  finalizeError: null,
  finalizeResult: null, // { enterpriseId, tenantUrl }

  // Best-effort website enrichment (logo + brand color + city/state). Populated
  // when the founder enters their existing website on the Business step; read
  // by the Business + Branding steps to prefill (always overridable).
  enrichment: null,
  enriching: false,
  enrichError: null,
};

const onboarding = (state = initialState, action) => {
  switch (action.type) {
    // ── Step 1 ────────────────────────────────────────────────────
    case REGISTER_ACCOUNTS_USER:
      return { ...state, registerLoading: true, registerError: null };
    case REGISTER_ACCOUNTS_USER_SUCCESS:
      return { ...state, registerLoading: false, registerError: null };
    case REGISTER_ACCOUNTS_USER_FAILURE:
      return {
        ...state,
        registerLoading: false,
        registerError: action.payload,
      };

    // ── Hydrate ───────────────────────────────────────────────────
    case ONBOARDING_HYDRATE_REQUEST:
      return { ...state, hydrating: true, hydrateError: null };
    case ONBOARDING_HYDRATE_SUCCESS:
      return {
        ...state,
        hydrating: false,
        hydrated: true,
        hydrateError: null,
        currentStep: action.payload?.currentStep || "business",
        business: action.payload?.business || null,
        production: action.payload?.production || null,
        sales: action.payload?.sales || null,
        branding: action.payload?.branding || null,
        enterpriseId: action.payload?.enterpriseId || null,
        pricingTableIds: action.payload?.pricingTableIds || [],
        blankProducts: action.payload?.blankProducts || [],
        categoryConfig: action.payload?.categoryConfig || {},
        createStore: action.payload?.createStore || false,
        enrichment: action.payload?.enrichment || null,
      };
    case ONBOARDING_HYDRATE_FAILURE:
      return { ...state, hydrating: false, hydrateError: action.payload };

    // ── Business step ─────────────────────────────────────────────
    case ONBOARDING_SAVE_BUSINESS_REQUEST:
      return { ...state, businessSaving: true, businessError: null };
    case ONBOARDING_SAVE_BUSINESS_SUCCESS:
      return {
        ...state,
        businessSaving: false,
        businessError: null,
        business: action.payload,
        currentStep: "production",
      };
    case ONBOARDING_SAVE_BUSINESS_FAILURE:
      return {
        ...state,
        businessSaving: false,
        businessError: action.payload,
      };

    // ── Production step ───────────────────────────────────────────
    case ONBOARDING_SAVE_PRODUCTION_REQUEST:
      return { ...state, productionSaving: true, productionError: null };
    case ONBOARDING_SAVE_PRODUCTION_SUCCESS:
      return {
        ...state,
        productionSaving: false,
        productionError: null,
        production: action.payload,
        currentStep: "sales",
      };
    case ONBOARDING_SAVE_PRODUCTION_FAILURE:
      return {
        ...state,
        productionSaving: false,
        productionError: action.payload,
      };

    // ── Sales step ────────────────────────────────────────────────
    case ONBOARDING_SAVE_SALES_REQUEST:
      return { ...state, salesSaving: true, salesError: null };
    case ONBOARDING_SAVE_SALES_SUCCESS:
      return {
        ...state,
        salesSaving: false,
        salesError: null,
        sales: action.payload,
        currentStep: "branding",
      };
    case ONBOARDING_SAVE_SALES_FAILURE:
      return {
        ...state,
        salesSaving: false,
        salesError: action.payload,
      };

    // ── Finalize ──────────────────────────────────────────────────
    case ONBOARDING_FINALIZE_REQUEST:
      return { ...state, finalizing: true, finalizeError: null };
    case ONBOARDING_FINALIZE_SUCCESS:
      return {
        ...state,
        finalizing: false,
        finalizeError: null,
        finalizeResult: action.payload,
        currentStep: "complete",
      };
    case ONBOARDING_FINALIZE_FAILURE:
      return { ...state, finalizing: false, finalizeError: action.payload };

    // ── Branding / URL step (creates the draft enterprise) ────────
    case ONBOARDING_SAVE_BRANDING_REQUEST:
      return { ...state, brandingSaving: true, brandingError: null };
    case ONBOARDING_SAVE_BRANDING_SUCCESS:
      return {
        ...state,
        brandingSaving: false,
        brandingError: null,
        branding: action.payload?.branding || state.branding,
        enterpriseId: action.payload?.enterpriseId || state.enterpriseId,
        currentStep: "pricing",
      };
    case ONBOARDING_SAVE_BRANDING_FAILURE:
      return { ...state, brandingSaving: false, brandingError: action.payload };

    // ── Pricing step ──────────────────────────────────────────────
    case ONBOARDING_SAVE_PRICING_REQUEST:
      return { ...state, pricingSaving: true, pricingError: null };
    case ONBOARDING_SAVE_PRICING_SUCCESS:
      return {
        ...state,
        pricingSaving: false,
        pricingError: null,
        pricingTableIds: action.payload?.pricingTableIds || [],
        currentStep: "products",
      };
    case ONBOARDING_SAVE_PRICING_FAILURE:
      return { ...state, pricingSaving: false, pricingError: action.payload };

    // ── Products step ─────────────────────────────────────────────
    case ONBOARDING_SAVE_PRODUCTS_REQUEST:
      return { ...state, productsSaving: true, productsError: null };
    case ONBOARDING_SAVE_PRODUCTS_SUCCESS:
      return {
        ...state,
        productsSaving: false,
        productsError: null,
        blankProducts: action.payload?.blankProducts || [],
        currentStep: "printOptions",
      };
    case ONBOARDING_SAVE_PRODUCTS_FAILURE:
      return { ...state, productsSaving: false, productsError: action.payload };

    // ── Print options step ────────────────────────────────────────
    case ONBOARDING_SAVE_PRINT_OPTIONS_REQUEST:
      return { ...state, printOptionsSaving: true, printOptionsError: null };
    case ONBOARDING_SAVE_PRINT_OPTIONS_SUCCESS:
      return {
        ...state,
        printOptionsSaving: false,
        printOptionsError: null,
        categoryConfig: action.payload?.categoryConfig || {},
        currentStep: "store",
      };
    case ONBOARDING_SAVE_PRINT_OPTIONS_FAILURE:
      return {
        ...state,
        printOptionsSaving: false,
        printOptionsError: action.payload,
      };

    // ── Store step ────────────────────────────────────────────────
    case ONBOARDING_SAVE_STORE_REQUEST:
      return { ...state, storeSaving: true, storeError: null };
    case ONBOARDING_SAVE_STORE_SUCCESS:
      return {
        ...state,
        storeSaving: false,
        storeError: null,
        createStore: !!action.payload?.createStore,
      };
    case ONBOARDING_SAVE_STORE_FAILURE:
      return { ...state, storeSaving: false, storeError: action.payload };

    // ── Website enrichment ────────────────────────────────────────
    case ONBOARDING_ENRICH_WEBSITE_REQUEST:
      return { ...state, enriching: true, enrichError: null };
    case ONBOARDING_ENRICH_WEBSITE_SUCCESS:
      return {
        ...state,
        enriching: false,
        enrichError: null,
        enrichment: action.payload || null,
      };
    case ONBOARDING_ENRICH_WEBSITE_FAILURE:
      return { ...state, enriching: false, enrichError: action.payload };

    default:
      return state;
  }
};

export default onboarding;
